import { LicenceId } from '@ps/platform-message-framework/common/LicenceId';
import { Translation } from '@ps/web-shared-components';
import React from 'react';
import { useField } from 'react-final-form';
import { useSelector } from 'react-redux';

import CurrencyAmount from '@swf/components/CurrencyAmount/CurrencyAmount';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import { select } from '@swf/core/store';

import type { PaySystemDetails } from '../../../../../../types/Cashier';
import {
  convertAmountByRate,
  getConversionRate,
  isZeroExponentCurrency
} from '../../../../../../utils/cashier';
import { calculateFee } from '../../../../../../utils/fee';

import {
  TXTWCL_This_deposit_includes_a_fee,
  TXTWCL_This_deposit_includes_a_fee_of_ELL,
  TXTWCL_We_will_add_N0_to_your_account_ELL
} from './DepositFee.messages.json';
import styles from './DepositFee.scss';

const DepositFee: React.FC = () => {
  const license = useSelector(select.account.getLicense);
  const {
    data: { paySystemDetails }
  } = useWizardContext();

  const {
    rates: { curr: balanceCurrency, rates },
    paysysFee
  }: PaySystemDetails = paySystemDetails;

  const {
    input: { value: currency }
  } = useField('deposit.currency', { subscription: { value: true } });

  const {
    input: {
      value: { amount }
    }
  } = useField('deposit', { subscription: { value: true } });

  const conversionRate = getConversionRate(rates, currency);
  const fee = calculateFee(
    convertAmountByRate(Number(amount || 0), conversionRate, balanceCurrency, false),
    paysysFee
  );

  const feeText = () => {
    if (amount) {
      return (
        <Translation
          tagName="p"
          {...TXTWCL_This_deposit_includes_a_fee_of_ELL}
          values={{
            0: (
              <CurrencyAmount
                value={fee * 100}
                currency={balanceCurrency}
                isZeroExponentCurrency={isZeroExponentCurrency(balanceCurrency)}
              />
            )
          }}
        />
      );
    }

    return <Translation tagName="p" {...TXTWCL_This_deposit_includes_a_fee} />;
  };

  return paysysFee.paysysFeeApplies &&
    paysysFee.leftGraceDeposits === 0 &&
    license !== LicenceId.ROMANIA ? (
    <div className={styles.container}>
      {feeText()}
      {!!amount && (
        <Translation
          tagName="b"
          {...TXTWCL_We_will_add_N0_to_your_account_ELL}
          values={{
            0: (
              <CurrencyAmount
                value={convertAmountByRate(amount, conversionRate, balanceCurrency, false) - fee}
                isZeroExponentCurrency={isZeroExponentCurrency(balanceCurrency)}
                currency={balanceCurrency}
                dollarValue
              />
            )
          }}
        />
      )}
    </div>
  ) : null;
};

export default DepositFee;
